/**
 * Countdown Validation Utilities
 *
 * Reusable validation functions for countdown components that handle:
 * - Past date validation with warning toasts
 * - Unit visibility validation to prevent hiding all units
 * - URL format validation for redirect functionality
 *
 * All validation functions automatically show toast notifications
 * and return validation results for conditional logic.
 *
 * @example
 * ```typescript
 * // Validate past date
 * validatePastDate(dateTime, 'standard', dispatch);
 *
 * // Validate unit visibility
 * const shouldBlock = validateUnitVisibility(
 *   'days', showDays, showHours, showMinutes, showSeconds, 'hide', dispatch
 * );
 * if (shouldBlock) return;
 *
 * // Validate URL
 * const { isValid, sanitizedUrl } = validateRedirectUrl(url, dispatch);
 * ```
 */

import type { AppDispatch } from '@/store/store';
import { showToast } from '@/store/notifications/slice';

/**
 * Validates if a date/time is in the past and shows a warning toast
 *
 * Only validates when mode is 'standard'. Returns false for other modes.
 * Toast auto-dismisses after 5 seconds.
 *
 * @param dateTime - ISO date string or Date object to validate
 * @param mode - Countdown mode ('standard', 'daily', or 'evergreen')
 * @param dispatch - Redux dispatch function
 * @returns true if date is in the past, false otherwise
 */
export const validatePastDate = (
  dateTime: string | Date,
  mode: 'standard' | 'daily' | 'evergreen',
  dispatch: AppDispatch,
): boolean => {
  if (mode !== 'standard') {
    return false;
  }

  const targetDate = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  const isPast = targetDate < new Date();

  if (isPast) {
    dispatch(
      showToast({
        text: 'This date has passed. The countdown will show 00:00:00:00 until you update it.',
        state: 'warning',
        icon: 'warning',
      }),
    );
  }

  return isPast;
};

/**
 * Validates unit visibility and shows error toast if trying to hide the last visible unit
 *
 * Ensures at least one time unit (days, hours, minutes, or seconds) remains visible.
 * Shows error toast when attempting to hide the last visible unit.
 * Toast auto-dismisses after 5 seconds.
 *
 * @param currentUnit - The unit being toggled ('days' | 'hours' | 'minutes' | 'seconds')
 * @param showDays - Whether days are currently visible
 * @param showHours - Whether hours are currently visible
 * @param showMinutes - Whether minutes are currently visible
 * @param showSeconds - Whether seconds are currently visible
 * @param newValue - The new visibility value ('show' | 'hide')
 * @param dispatch - Redux dispatch function
 * @returns true if the action should be blocked, false if allowed
 */
export const validateUnitVisibility = (
  currentUnit: 'days' | 'hours' | 'minutes' | 'seconds',
  showDays: boolean,
  showHours: boolean,
  showMinutes: boolean,
  showSeconds: boolean,
  newValue: 'show' | 'hide',
  dispatch: AppDispatch,
): boolean => {
  if (newValue === 'show') {
    return false; // Always allow showing units
  }

  // Count currently visible units
  const visibleUnits = [showDays, showHours, showMinutes, showSeconds].filter(Boolean).length;

  // Check if this is the last visible unit
  const unitVisibilityMap = {
    days: showDays,
    hours: showHours,
    minutes: showMinutes,
    seconds: showSeconds,
  };

  const isLastVisibleUnit = visibleUnits === 1 && unitVisibilityMap[currentUnit];

  if (isLastVisibleUnit) {
    dispatch(
      showToast({
        text: 'At least one time unit must be visible.',
        state: 'error',
        icon: 'error',
      }),
    );
    return true; // Block the action
  }

  return false; // Allow the action
};

/**
 * Validates URL format and shows appropriate error toasts
 *
 * Performs the following validations:
 * - Empty URL: Shows error toast
 * - Missing protocol: Auto-fixes by adding 'https://'
 * - Invalid format: Shows error toast
 *
 * Toast auto-dismisses after 5 seconds.
 *
 * @param url - URL string to validate
 * @param dispatch - Redux dispatch function
 * @returns Object with { isValid: boolean, sanitizedUrl: string }
 */
export const validateRedirectUrl = (
  url: string,
  dispatch: AppDispatch,
): { isValid: boolean; sanitizedUrl: string } => {
  const trimmedUrl = url.trim();

  // If empty, show toast
  if (!trimmedUrl) {
    dispatch(
      showToast({
        text: 'Enter a URL or select a different redirect option.',
        state: 'error',
        icon: 'error',
      }),
    );
    return { isValid: false, sanitizedUrl: trimmedUrl };
  }

  // Check if URL starts with http:// or https://
  const hasProtocol = trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://');

  if (!hasProtocol) {
    // Auto-fix: add https:// prefix
    const sanitizedUrl = `https://${trimmedUrl}`;
    return { isValid: true, sanitizedUrl };
  }

  return { isValid: true, sanitizedUrl: trimmedUrl };
};
